import { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  Database, 
  FileSpreadsheet, 
  Trash2, 
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Film,
  Tv,
  X
} from 'lucide-react';
import TopBar from '@/components/TopBar';
import importService from '../services/importService';
import type { 
  ImportStats, 
  OneStudioChannel, 
  OneStudioVideo,
  ImportResult 
} from '../services/importService';

type TabType = 'overview' | 'channels' | 'videos';
type SourceType = '1studio' | 'ott-studio';

export default function ImportPage() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [sourceType, setSourceType] = useState<SourceType>('1studio');
  const [stats, setStats] = useState<ImportStats>({ channels: 0, videos: 0 });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState<'channels' | 'videos' | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Channels state
  const [channels, setChannels] = useState<OneStudioChannel[]>([]);
  const [channelsTotal, setChannelsTotal] = useState(0);
  const [channelsPage, setChannelsPage] = useState(0);
  
  // Videos state
  const [videos, setVideos] = useState<OneStudioVideo[]>([]);
  const [videosTotal, setVideosTotal] = useState(0);
  const [videosPage, setVideosPage] = useState(0);
  const [selectedChannelId, setSelectedChannelId] = useState<number | undefined>(undefined);
  
  // Modal state
  const [selectedVideo, setSelectedVideo] = useState<OneStudioVideo | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<OneStudioChannel | null>(null);
  
  const channelsFileRef = useRef<HTMLInputElement>(null);
  const videosFileRef = useRef<HTMLInputElement>(null);
  
  const pageSize = 50;

  useEffect(() => {
    loadStats();
  }, [sourceType]);

  useEffect(() => {
    if (activeTab === 'channels') {
      loadChannels();
    } else if (activeTab === 'videos') {
      loadVideos();
    }
  }, [activeTab, channelsPage, videosPage, selectedChannelId, sourceType]);

  const loadStats = async () => {
    try {
      const data = await importService.getStats(sourceType);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadChannels = async () => {
    setLoading(true);
    try {
      const response = await importService.getChannels(pageSize, channelsPage * pageSize, sourceType);
      setChannels(response.data);
      setChannelsTotal(response.total);
    } catch (err) {
      console.error('Failed to load channels:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async () => {
    setLoading(true);
    try {
      const response = await importService.getVideos(pageSize, videosPage * pageSize, selectedChannelId, sourceType);
      setVideos(response.data);
      setVideosTotal(response.total);
    } catch (err) {
      console.error('Failed to load videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportChannels = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting('channels');
    setError(null);
    setImportResult(null);
    
    try {
      const result = await importService.importChannels(file, sourceType);
      setImportResult(result);
      loadStats();
      if (activeTab === 'channels') loadChannels();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import channels');
    } finally {
      setImporting(null);
      if (channelsFileRef.current) channelsFileRef.current.value = '';
    }
  };

  const handleImportVideos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting('videos');
    setError(null);
    setImportResult(null);
    
    try {
      const result = await importService.importVideos(file, sourceType);
      setImportResult(result);
      loadStats();
      if (activeTab === 'videos') loadVideos();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import videos');
    } finally {
      setImporting(null);
      if (videosFileRef.current) videosFileRef.current.value = '';
    }
  };

  const handleClearData = async (type: 'channels' | 'videos' | 'all') => {
    if (!confirm(`Are you sure you want to clear ${type === 'all' ? 'all imported' : type} data?`)) {
      return;
    }
    
    try {
      await importService.clearData(type);
      loadStats();
      if (type === 'channels' || type === 'all') {
        setChannels([]);
        setChannelsTotal(0);
      }
      if (type === 'videos' || type === 'all') {
        setVideos([]);
        setVideosTotal(0);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clear data');
    }
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Database className="w-8 h-8 text-cyan-400" />
                Studio Data Import
              </h1>
              <p className="text-gray-600 mt-2">
                Import channels and videos from 1Studio or OTT-Studio CSV exports
              </p>
            </div>
            
            {/* Source Type Selector */}
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setSourceType('1studio')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  sourceType === '1studio'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                  1Studio
                </span>
              </button>
              <button
                onClick={() => setSourceType('ott-studio')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  sourceType === 'ott-studio'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                  OTT-Studio
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className={`bg-white border rounded-xl p-6 ${sourceType === '1studio' ? 'border-blue-200' : 'border-purple-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceType === '1studio' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {sourceType === '1studio' ? '1Studio' : 'OTT'}
                  </span>
                  Channels
                </p>
                <p className={`text-3xl font-bold ${sourceType === '1studio' ? 'text-blue-500' : 'text-purple-500'}`}>{stats.channels.toLocaleString()}</p>
              </div>
              <Tv className={`w-10 h-10 ${sourceType === '1studio' ? 'text-blue-400/30' : 'text-purple-400/30'}`} />
            </div>
          </div>
          <div className={`bg-white border rounded-xl p-6 ${sourceType === '1studio' ? 'border-blue-200' : 'border-purple-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceType === '1studio' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {sourceType === '1studio' ? '1Studio' : 'OTT'}
                  </span>
                  Videos
                </p>
                <p className={`text-3xl font-bold ${sourceType === '1studio' ? 'text-blue-500' : 'text-purple-500'}`}>{stats.videos.toLocaleString()}</p>
              </div>
              <Film className={`w-10 h-10 ${sourceType === '1studio' ? 'text-blue-400/30' : 'text-purple-400/30'}`} />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Actions</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => loadStats()}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Refresh stats"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleClearData('all')}
                    className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                    title="Clear all data"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <Database className="w-10 h-10 text-gray-700" />
            </div>
          </div>
        </div>

        {/* Import Result / Error Messages */}
        {importResult && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-green-700 font-medium">{importResult.message}</p>
              <p className="text-green-600 text-sm mt-1">
                Parsed: {importResult.total_parsed} | Valid: {importResult.valid_records} | Inserted: {importResult.inserted}
              </p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {(['overview', 'channels', 'videos'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-medium capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'text-blue-600 border-blue-600 bg-white'
                  : 'text-gray-600 border-transparent hover:text-gray-500'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Import Channels */}
            <div className={`bg-white border rounded-xl p-6 ${sourceType === '1studio' ? 'border-blue-200' : 'border-purple-200'}`}>
              <div className="flex items-center gap-3 mb-4">
                <FileSpreadsheet className={`w-6 h-6 ${sourceType === '1studio' ? 'text-blue-500' : 'text-purple-500'}`} />
                <h2 className="text-xl font-semibold">Import Channels</h2>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceType === '1studio' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Upload a CSV file with channel data from {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'} export.
              </p>
              <input
                ref={channelsFileRef}
                type="file"
                accept=".csv"
                onChange={handleImportChannels}
                className="hidden"
              />
              <button
                onClick={() => channelsFileRef.current?.click()}
                disabled={importing !== null}
                className={`w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors ${
                  sourceType === '1studio' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {importing === 'channels' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Importing to {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'}...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload channels.csv
                  </>
                )}
              </button>
              {stats.channels > 0 && (
                <button
                  onClick={() => handleClearData('channels')}
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Channels ({stats.channels})
                </button>
              )}
            </div>

            {/* Import Videos */}
            <div className={`bg-white border rounded-xl p-6 ${sourceType === '1studio' ? 'border-blue-200' : 'border-purple-200'}`}>
              <div className="flex items-center gap-3 mb-4">
                <FileSpreadsheet className={`w-6 h-6 ${sourceType === '1studio' ? 'text-blue-500' : 'text-purple-500'}`} />
                <h2 className="text-xl font-semibold">Import Videos</h2>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceType === '1studio' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Upload a CSV file with video data from {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'} export.
              </p>
              <input
                ref={videosFileRef}
                type="file"
                accept=".csv"
                onChange={handleImportVideos}
                className="hidden"
              />
              <button
                onClick={() => videosFileRef.current?.click()}
                disabled={importing !== null}
                className={`w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors ${
                  sourceType === '1studio' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {importing === 'videos' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Importing to {sourceType === '1studio' ? '1Studio' : 'OTT-Studio'}...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload videos.csv
                  </>
                )}
              </button>
              {stats.videos > 0 && (
                <button
                  onClick={() => handleClearData('videos')}
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Videos ({stats.videos.toLocaleString()})
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'channels' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              </div>
            ) : channels.length === 0 ? (
              <div className="text-center py-12 text-gray-600">
                <Tv className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No channels imported yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">ID</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Title</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Format</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Timezone</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Source</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {channels.map((channel) => (
                        <tr 
                          key={channel.id} 
                          onClick={() => setSelectedChannel(channel)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4 text-gray-700">{channel.id}</td>
                          <td className="py-3 px-4 text-gray-900 font-medium">{channel.title || '-'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              channel.format === 'hd' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                            }`}>
                              {channel.format?.toUpperCase() || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{channel.timezone || '-'}</td>
                          <td className="py-3 px-4 text-gray-600">{channel.source || '-'}</td>
                          <td className="py-3 px-4 text-gray-600">{formatDate(channel.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <p className="text-gray-600 text-sm">
                    Showing {channelsPage * pageSize + 1} - {Math.min((channelsPage + 1) * pageSize, channelsTotal)} of {channelsTotal}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setChannelsPage(p => Math.max(0, p - 1))}
                      disabled={channelsPage === 0}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setChannelsPage(p => p + 1)}
                      disabled={(channelsPage + 1) * pageSize >= channelsTotal}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'videos' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Channel Filter */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <label htmlFor="channelIdFilter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Filter by Channel ID:
                </label>
                <input
                  id="channelIdFilter"
                  type="number"
                  placeholder="Enter channel ID (leave empty for all)"
                  value={selectedChannelId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedChannelId(value ? parseInt(value) : undefined);
                    setVideosPage(0);
                  }}
                  className="flex-1 max-w-xs bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {selectedChannelId && (
                  <button
                    onClick={() => {
                      setSelectedChannelId(undefined);
                      setVideosPage(0);
                    }}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-12 text-gray-600">
                <Film className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No videos imported yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">ID</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Title</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Channel</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Duration</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Resolution</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Format</th>
                        <th className="text-left py-3 px-4 text-gray-600 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {videos.map((video) => (
                        <tr 
                          key={video.id} 
                          onClick={() => setSelectedVideo(video)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4 text-gray-700">{video.id}</td>
                          <td className="py-3 px-4 text-gray-900 font-medium max-w-xs truncate" title={video.title || ''}>
                            {video.title || '-'}
                          </td>
                          <td className="py-3 px-4 text-gray-600">{video.channel_id || '-'}</td>
                          <td className="py-3 px-4 text-gray-700">{formatDuration(video.duration)}</td>
                          <td className="py-3 px-4 text-gray-600">
                            {video.hd_width && video.hd_height ? `${video.hd_width}x${video.hd_height}` : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-gray-200 rounded text-xs text-gray-700">
                              {video.video_format?.toUpperCase() || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{formatDate(video.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <p className="text-gray-600 text-sm">
                    Showing {videosPage * pageSize + 1} - {Math.min((videosPage + 1) * pageSize, videosTotal)} of {videosTotal.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setVideosPage(p => Math.max(0, p - 1))}
                      disabled={videosPage === 0}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setVideosPage(p => p + 1)}
                      disabled={(videosPage + 1) * pageSize >= videosTotal}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Channel Details Modal */}
      {selectedChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div>
                <h2 className="text-2xl font-bold text-white">Channel Details</h2>
                <p className="text-slate-400 mt-1">ID: {selectedChannel.id}</p>
              </div>
              <button
                onClick={() => setSelectedChannel(null)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Info */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-3">Basic Information</h3>
                  <div className="space-y-2">
                    <DetailRow label="Title" value={selectedChannel.title} />
                    <DetailRow label="Stream Name" value={selectedChannel.stream} />
                    <DetailRow label="Stream URL" value={selectedChannel.stream_url} />
                    <DetailRow label="Company ID" value={selectedChannel.company_id} />
                    <DetailRow label="Channel Type" value={selectedChannel.channel_type} />
                    <DetailRow label="Source" value={selectedChannel.source} />
                  </div>
                </div>

                {/* Technical Specs */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-400 mb-3">Technical Specifications</h3>
                  <div className="space-y-2">
                    <DetailRow label="Format" value={selectedChannel.format?.toUpperCase()} />
                    <DetailRow label="Timezone" value={selectedChannel.timezone} />
                    <DetailRow label="Type" value={selectedChannel.type} />
                    <DetailRow label="Suspended" value={selectedChannel.suspended ? 'Yes' : 'No'} />
                  </div>
                </div>

                {/* Bunny CDN */}
                <div>
                  <h3 className="text-lg font-semibold text-orange-400 mb-3">Bunny CDN</h3>
                  <div className="space-y-2">
                    <DetailRow label="Collection Name" value={selectedChannel.bunny_stream_library_collection_name} />
                    <DetailRow label="Collection ID" value={selectedChannel.bunny_stream_library_collection_id} />
                    <DetailRow label="Multi ID" value={selectedChannel.bunny_stream_multi_id} />
                  </div>
                </div>

                {/* S3 Storage */}
                <div>
                  <h3 className="text-lg font-semibold text-green-400 mb-3">S3 Storage</h3>
                  <div className="space-y-2">
                    <DetailRow label="S3 CDN" value={selectedChannel.s3cdn} />
                    <DetailRow label="S3 Bucket" value={selectedChannel.s3bucket} />
                    <DetailRow label="S3 Folder" value={selectedChannel.s3folder} />
                    <DetailRow label="S3 Host" value={selectedChannel.s3host} />
                  </div>
                </div>

                {/* Platform URLs */}
                <div>
                  <h3 className="text-lg font-semibold text-blue-400 mb-3">Platform URLs</h3>
                  <div className="space-y-2">
                    <DetailRow label="Apple TV" value={selectedChannel.apple_tv_url} />
                    <DetailRow label="Amazon Fire" value={selectedChannel.amazon_fire_url} />
                    <DetailRow label="Roku TV" value={selectedChannel.roku_tv_url} />
                    <DetailRow label="Mobile Web" value={selectedChannel.mobile_web_url} />
                    <DetailRow label="Launchpad" value={selectedChannel.launchpad_url} />
                    <DetailRow label="Shopify" value={selectedChannel.shopify_url} />
                  </div>
                </div>

                {/* Display Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-3">Display Settings</h3>
                  <div className="space-y-2">
                    <DetailRow label="Display Roku" value={selectedChannel.display_roku ? 'Yes' : 'No'} />
                    <DetailRow label="Display Apple TV" value={selectedChannel.display_appletv ? 'Yes' : 'No'} />
                    <DetailRow label="Display Fire TV" value={selectedChannel.display_firetv ? 'Yes' : 'No'} />
                    <DetailRow label="Display Mobile Web" value={selectedChannel.display_mobileweb ? 'Yes' : 'No'} />
                    <DetailRow label="Display CDN" value={selectedChannel.display_cdn ? 'Yes' : 'No'} />
                    <DetailRow label="Display Show" value={selectedChannel.display_show ? 'Yes' : 'No'} />
                  </div>
                </div>

                {/* VAST Tags */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-pink-400 mb-3">VAST Ad Tags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <DetailRow label="VAST Tag" value={selectedChannel.vast_tag} />
                    <DetailRow label="Roku" value={selectedChannel.vast_tag_roku} />
                    <DetailRow label="Fire TV" value={selectedChannel.vast_tag_firetv} />
                    <DetailRow label="Apple TV" value={selectedChannel.vast_tag_appletv} />
                    <DetailRow label="iOS" value={selectedChannel.vast_tag_ios} />
                    <DetailRow label="Android" value={selectedChannel.vast_tag_android} />
                    <DetailRow label="LG" value={selectedChannel.vast_tag_lg} />
                    <DetailRow label="Samsung" value={selectedChannel.vast_tag_samsung} />
                  </div>
                </div>

                {/* Additional Info */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-indigo-400 mb-3">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <DetailRow label="Logo Extension" value={selectedChannel.logo_ext} />
                    <DetailRow label="Tracking ID" value={selectedChannel.tracking_id} />
                    <DetailRow label="Streamlyzer Token" value={selectedChannel.streamlyzer_token} />
                    <DetailRow label="IP Address" value={selectedChannel.ip_address} />
                    <DetailRow label="Pinned" value={selectedChannel.pinned ? 'Yes' : 'No'} />
                    <DetailRow label="Sort Order" value={selectedChannel.sort_order} />
                    <DetailRow label="Created At" value={formatDate(selectedChannel.created_at)} />
                    <DetailRow label="Updated At" value={formatDate(selectedChannel.updated_at)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Details Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div>
                <h2 className="text-2xl font-bold text-white">Video Details</h2>
                <p className="text-slate-400 mt-1">ID: {selectedVideo.id}</p>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Info */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-3">Basic Information</h3>
                  <div className="space-y-2">
                    <DetailRow label="Title" value={selectedVideo.title} />
                    <DetailRow label="Internal Name" value={selectedVideo.internal_name} />
                    <DetailRow label="Description" value={selectedVideo.description} />
                    <DetailRow label="Channel ID" value={selectedVideo.channel_id} />
                    <DetailRow label="Playlist ID" value={selectedVideo.playlist_id} />
                  </div>
                </div>

                {/* Video Specifications */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-400 mb-3">Video Specifications</h3>
                  <div className="space-y-2">
                    <DetailRow label="Duration" value={formatDuration(selectedVideo.duration)} />
                    <DetailRow label="Format" value={selectedVideo.video_format} />
                    <DetailRow label="File Name" value={selectedVideo.file_name} />
                    <DetailRow label="Job ID" value={selectedVideo.job_id} />
                    <DetailRow label="Encode Status" value={selectedVideo.encode_status} />
                    <DetailRow label="Type" value={selectedVideo.type} />
                  </div>
                </div>

                {/* HD Quality */}
                <div>
                  <h3 className="text-lg font-semibold text-green-400 mb-3">HD Quality</h3>
                  <div className="space-y-2">
                    <DetailRow label="Resolution" value={selectedVideo.hd_width && selectedVideo.hd_height ? `${selectedVideo.hd_width}x${selectedVideo.hd_height}` : null} />
                    <DetailRow label="File Size" value={selectedVideo.hd_file_size ? `${(selectedVideo.hd_file_size / 1024 / 1024).toFixed(2)} MB` : null} />
                    <DetailRow label="Video Bitrate" value={selectedVideo.hd_video_bitrate ? `${selectedVideo.hd_video_bitrate} kbps` : null} />
                    <DetailRow label="Video Codec" value={selectedVideo.hd_video_codec} />
                    <DetailRow label="Audio Codec" value={selectedVideo.hd_audio_codec} />
                    <DetailRow label="MIME Type" value={selectedVideo.hd_mime_type} />
                  </div>
                </div>

                {/* SD Quality */}
                <div>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-3">SD Quality</h3>
                  <div className="space-y-2">
                    <DetailRow label="File Name" value={selectedVideo.sd_file_name} />
                    <DetailRow label="Resolution" value={selectedVideo.sd_width && selectedVideo.sd_height ? `${selectedVideo.sd_width}x${selectedVideo.sd_height}` : null} />
                    <DetailRow label="File Size" value={selectedVideo.sd_file_size ? `${(selectedVideo.sd_file_size / 1024 / 1024).toFixed(2)} MB` : null} />
                    <DetailRow label="Video Bitrate" value={selectedVideo.sd_video_bitrate ? `${selectedVideo.sd_video_bitrate} kbps` : null} />
                    <DetailRow label="Video Codec" value={selectedVideo.sd_video_codec} />
                    <DetailRow label="Audio Codec" value={selectedVideo.sd_audio_codec} />
                  </div>
                </div>

                {/* Mobile Quality */}
                <div>
                  <h3 className="text-lg font-semibold text-orange-400 mb-3">Mobile Quality</h3>
                  <div className="space-y-2">
                    <DetailRow label="File Name" value={selectedVideo.mb_file_name} />
                    <DetailRow label="Resolution" value={selectedVideo.mb_width && selectedVideo.mb_height ? `${selectedVideo.mb_width}x${selectedVideo.mb_height}` : null} />
                    <DetailRow label="File Size" value={selectedVideo.mb_file_size ? `${(selectedVideo.mb_file_size / 1024 / 1024).toFixed(2)} MB` : null} />
                    <DetailRow label="Video Bitrate" value={selectedVideo.mb_video_bitrate ? `${selectedVideo.mb_video_bitrate} kbps` : null} />
                  </div>
                </div>

                {/* Metadata */}
                <div>
                  <h3 className="text-lg font-semibold text-pink-400 mb-3">Metadata</h3>
                  <div className="space-y-2">
                    <DetailRow label="Thumbnail" value={selectedVideo.thumbnail_name} />
                    <DetailRow label="Rating" value={selectedVideo.rating} />
                    <DetailRow label="Director" value={selectedVideo.director} />
                    <DetailRow label="Actor 1" value={selectedVideo.actor1} />
                    <DetailRow label="Actor 2" value={selectedVideo.actor2} />
                    <DetailRow label="Actor 3" value={selectedVideo.actor3} />
                  </div>
                </div>

                {/* Storage & Source */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-blue-400 mb-3">Storage & Source</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <DetailRow label="Source" value={selectedVideo.source} />
                    <DetailRow label="Storage" value={selectedVideo.storage ? `${(selectedVideo.storage / 1024 / 1024).toFixed(2)} MB` : null} />
                    <DetailRow label="HLS URL" value={selectedVideo.hls_url} />
                    <DetailRow label="Bunny Video ID" value={selectedVideo.bunny_stream_video_id} />
                    <DetailRow label="Created At" value={formatDate(selectedVideo.created_at)} />
                    <DetailRow label="Updated At" value={formatDate(selectedVideo.updated_at)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for detail rows
function DetailRow({ label, value }: { label: string; value: any }) {
  const displayValue = value === null || value === undefined || value === '' || value === 0 ? '-' : String(value);
  const isLongValue = displayValue.length > 50;
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
      <span className="text-slate-400 text-sm font-medium min-w-[140px]">{label}:</span>
      <span className={`text-slate-200 text-sm ${isLongValue ? 'break-all' : ''}`} title={displayValue}>
        {displayValue}
      </span>
    </div>
  );
}

